import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'Landing',
      component: () => import('@/views/Landing.vue'),
      meta: { requiresAuth: false } // Public landing page
    },
    {
      path: '/login',
      name: 'Login',
      component: () => import('@/views/Login.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/setup',
      name: 'Setup',
      component: () => import('@/views/Setup.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/signup',
      name: 'Signup',
      component: () => import('@/views/Signup.vue'),
      meta: { requiresGuest: true }
    },
    {
      path: '/verify-email/:token',
      name: 'VerifyEmail',
      component: () => import('@/views/VerifyEmail.vue'),
      meta: { requiresAuth: false } // Public route
    },
    {
      path: '/forgot-password',
      name: 'ForgotPassword',
      component: () => import('@/views/ForgotPassword.vue'),
      meta: { requiresGuest: true } // Only for non-logged-in users
    },
    {
      path: '/reset-password/:token',
      name: 'ResetPassword',
      component: () => import('@/views/ResetPassword.vue'),
      meta: { requiresAuth: false } // Public route
    },
    {
      path: '/accept-invitation/:token',
      name: 'AcceptInvitation',
      component: () => import('@/views/AcceptInvitation.vue'),
      meta: { requiresAuth: false } // Public route — employee not logged in yet
    },
    {
      path: '/pricing',
      name: 'Pricing',
      component: () => import('@/views/Pricing.vue'),
      meta: { requiresAuth: false } // Available to everyone
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: () => import('@/views/Dashboard.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/users',
      name: 'Users',
      component: () => import('@/views/Users.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/systems',
      name: 'Systems',
      component: () => import('@/views/Systems.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/access',
      name: 'Access',
      component: () => import('@/views/Access.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/access-matrix',
      name: 'AccessMatrix',
      component: () => import('@/views/AccessMatrix.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/events',
      name: 'EventTimeline',
      component: () => import('@/views/EventTimeline.vue'),
      meta: { requiresAuth: true, notForEmployee: true }
    },
    {
      path: '/audit',
      name: 'Audit',
      component: () => import('@/views/Audit.vue'),
      meta: { requiresAuth: true, notForEmployee: true }
    },
    {
      path: '/compliance',
      name: 'Compliance',
      component: () => import('@/views/Compliance.vue'),
      meta: { requiresAuth: true, notForEmployee: true }
    },
    {
      path: '/integrations',
      name: 'Integrations',
      component: () => import('@/views/Integrations.vue'),
      meta: { requiresAuth: true, adminOnly: true }
    },
    {
      path: '/reports',
      name: 'Reports',
      component: () => import('@/views/Reports.vue'),
      meta: { requiresAuth: true, notForEmployee: true }
    },
    {
      path: '/billing',
      name: 'Billing',
      component: () => import('@/views/Billing.vue'),
      meta: { requiresAuth: true, billingManager: true }
    },
    {
      path: '/settings',
      name: 'Settings',
      component: () => import('@/views/Settings.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/departments',
      name: 'Departments',
      component: () => import('@/views/Departments.vue'),
      meta: { requiresAuth: true, adminOnly: true }
    },
    {
      path: '/privacy',
      name: 'PrivacyPolicy',
      component: () => import('@/views/PrivacyPolicy.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/terms',
      name: 'TermsOfService',
      component: () => import('@/views/TermsOfService.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/dpa',
      name: 'DPA',
      component: () => import('@/views/DPA.vue'),
      meta: { requiresAuth: false }
    }
  ]
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Если есть токен, но нет пользователя, попытаемся его загрузить
  if (authStore.token && !authStore.user && !authStore.loading) {
    authStore.loading = true
    try {
      await authStore.fetchUser()
    } catch (error) {
      console.error('Error loading user:', error)
    } finally {
      authStore.loading = false
    }
  }

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next('/login')
  } else if (to.meta.requiresGuest && authStore.isAuthenticated) {
    next('/dashboard')
  } else if (to.meta.adminOnly && authStore.user?.role !== 'ADMIN') {
    // Non-admins are redirected back to the dashboard — the matching nav
    // entry is also hidden in DashboardLayout.vue.
    next('/dashboard')
  } else if (to.meta.billingManager && !['ADMIN', 'HR'].includes(authStore.user?.role)) {
    next('/dashboard')
  } else if (to.meta.notForEmployee && authStore.user?.role === 'EMPLOYEE') {
    // Phase 3: EMPLOYEE cannot access events, audit, compliance, reports.
    next('/dashboard')
  } else {
    next()
  }
})

export default router
